import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import enMessages from "./messages/en.json";
import {
  DEFAULT_LOCALE,
  LOCALES,
  CURRENCY_FRACTION_DIGITS,
  type LocaleCode,
  type LocaleConfig,
  type CurrencyCode,
} from "./locales";

type Messages = Record<string, unknown>;

const MESSAGES: Record<LocaleCode, Messages> = {
  "en-GB": enMessages as Messages,
};

const STORAGE_KEY = "kitdex.locale";

let currentLocale: LocaleCode = DEFAULT_LOCALE;

export function getCurrentLocale(): LocaleCode {
  return currentLocale;
}

function readStoredLocale(): LocaleCode {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && LOCALES.some((l) => l.code === v)) return v as LocaleCode;
  } catch {
    /* ignore */
  }
  return DEFAULT_LOCALE;
}

function lookup(messages: Messages, key: string): string | undefined {
  const direct = (messages as Record<string, unknown>)[key];
  if (typeof direct === "string") return direct;
  const parts = key.split(".");
  let node: unknown = messages;
  for (const p of parts) {
    if (node && typeof node === "object" && p in (node as Record<string, unknown>)) {
      node = (node as Record<string, unknown>)[p];
    } else {
      return undefined;
    }
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : `{${name}}`,
  );
}

export function translate(
  locale: LocaleCode,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const msgs = MESSAGES[locale] ?? MESSAGES[DEFAULT_LOCALE];
  const found = lookup(msgs, key) ?? lookup(MESSAGES[DEFAULT_LOCALE], key);
  if (found === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing key: ${key}`);
    return key;
  }
  return interpolate(found, vars);
}

type I18nContextValue = {
  locale: LocaleCode;
  config: LocaleConfig;
  setLocale: (code: LocaleCode) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(() => {
    const initial = readStoredLocale();
    currentLocale = initial;
    return initial;
  });

  useEffect(() => {
    currentLocale = locale;
    const cfg = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];
    if (typeof document !== "undefined") {
      document.documentElement.lang = cfg.htmlLang;
      document.documentElement.dir = cfg.dir;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
  }, [locale]);

  const setLocale = useCallback((code: LocaleCode) => {
    setLocaleState(code);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale],
  );

  const config = useMemo(
    () => LOCALES.find((l) => l.code === locale) ?? LOCALES[0],
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, config, setLocale, t }),
    [locale, config, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}

// ---------- Locale-aware formatters ----------

export type Money = { amount: number; currency: string } | null | undefined;

export function formatMoney(money: Money, locale: LocaleCode = currentLocale): string {
  if (!money) return translate(locale, "common.noValue");
  const currency = money.currency as CurrencyCode;
  const fractionDigits = CURRENCY_FRACTION_DIGITS[currency] ?? 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: money.currency,
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    }).format(money.amount / 100);
  } catch {
    return `${money.currency} ${(money.amount / 100).toFixed(fractionDigits)}`;
  }
}

export function formatCurrencyAmount(
  amount: number,
  currency: string,
  locale: LocaleCode = currentLocale,
): string {
  const fractionDigits = CURRENCY_FRACTION_DIGITS[currency as CurrencyCode] ?? 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(fractionDigits)}`;
  }
}

export function useFormatCurrencyAmount() {
  const { locale } = useI18n();
  return useCallback(
    (amount: number, currency: string) => formatCurrencyAmount(amount, currency, locale),
    [locale],
  );
}

export function formatNumber(value: number, locale: LocaleCode = currentLocale): string {
  try {
    return new Intl.NumberFormat(locale).format(value);
  } catch {
    return String(value);
  }
}

export function formatDateShort(iso: string, locale: LocaleCode = currentLocale): string {
  const d = new Date(iso);
  try {
    return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(d);
  } catch {
    return d.toDateString();
  }
}

export function formatDateLong(iso: string, locale: LocaleCode = currentLocale): string {
  const d = new Date(iso);
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return d.toDateString();
  }
}

export function formatRelative(iso: string, locale: LocaleCode = currentLocale): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1) return translate(locale, "common.today");
  if (days === 1) return translate(locale, "common.yesterday");
  if (days < 7) return translate(locale, "common.daysAgoShort", { days });
  if (days < 30)
    return translate(locale, "common.weeksAgoShort", { weeks: Math.floor(days / 7) });
  return translate(locale, "common.monthsAgoShort", { months: Math.floor(days / 30) });
}

// ---------- Hook variants ----------

export function useFormatMoney() {
  const { locale } = useI18n();
  return useCallback((m: Money) => formatMoney(m, locale), [locale]);
}

export function useFormatDateShort() {
  const { locale } = useI18n();
  return useCallback((iso: string) => formatDateShort(iso, locale), [locale]);
}

export function useFormatRelative() {
  const { locale } = useI18n();
  return useCallback((iso: string) => formatRelative(iso, locale), [locale]);
}

export function useFormatNumber() {
  const { locale } = useI18n();
  return useCallback((n: number) => formatNumber(n, locale), [locale]);
}

export { LOCALES, DEFAULT_LOCALE } from "./locales";
export type { LocaleCode, LocaleConfig, CurrencyCode } from "./locales";
